/**
 * Unified AI treatment-plan launcher for the treatment-plan editor.
 *
 * - No plan yet → Quick generate (primary) + Guided interview
 * - Plan exists → Revise with AI (primary) + Guided interview (to draft another plan)
 */

import {
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconBolt, IconCheck, IconSparkles } from '@tabler/icons-react';
import { useOptionalAiAssistant } from '../../../features/ai-assistant';
import type { TreatmentPlanGeneratorSuccessResult } from '../../../lib/aiTreatmentPlanGeneratorService';
import type {
  EvidenceGroundedSymptom,
  TreatmentPlanClinicalContextDiagnosis,
  TreatmentPlanOperation,
} from '../../../lib/treatment-plan/evidenceGroundedSymptoms';

export interface AiTreatmentPlanGeneratorPanelProps {
  patientId: string;
  episodeId: string | undefined;
  /** Medplum resource id of the treatment-plan Questionnaire template. */
  questionnaireId: string | undefined;
  documentKey: string;
  /** Stable across /new → persisted response id for one editor mount. */
  editorSessionKey: string;
  /**
   * When true, the provider or client has opted out of Gemini/LLM-backed AI
   * documentation. Hides the planner launcher; shows a Disabled badge only.
   */
  geminiAiDisabled?: boolean;
  /** Called with the validated generation result; the parent applies it to the live form. */
  onPlanGenerated: (generated: TreatmentPlanGeneratorSuccessResult) => void;
  /** True after a quick/guided generation has been applied in this session. */
  generatedApplied?: boolean;
  /**
   * True when the draft already has a treatment plan (persisted, generated this
   * session, or provider-entered content). Switches the primary action to revise.
   */
  hasExistingPlan?: boolean;
  /** Diagnoses already on the plan, from the live treatment-plan form. */
  diagnoses?: TreatmentPlanClinicalContextDiagnosis[];
  /** Symptoms already documented in the live treatment-plan form. */
  symptoms?: EvidenceGroundedSymptom[];
  /** Best-effort: 'initial' for a new plan, 'revision' when one already exists. */
  operation?: TreatmentPlanOperation;
  treatmentFrequency?: string;
}

export function AiTreatmentPlanGeneratorPanel({
  patientId,
  episodeId,
  questionnaireId,
  documentKey,
  editorSessionKey,
  geminiAiDisabled = false,
  onPlanGenerated,
  generatedApplied = false,
  hasExistingPlan = false,
  diagnoses,
  symptoms,
  operation,
  treatmentFrequency,
}: AiTreatmentPlanGeneratorPanelProps): JSX.Element {
  const assistant = useOptionalAiAssistant();
  const ready = Boolean(episodeId && questionnaireId);
  const canRevise = Boolean(assistant?.revisionContext) && !geminiAiDisabled;
  const showRevise = hasExistingPlan;

  const openPlanner = (mode: 'guided' | 'quick') => {
    if (!assistant || !episodeId || !questionnaireId || geminiAiDisabled) return;
    const context = {
      kind: 'treatment-plan-guided-create' as const,
      mode,
      patientId,
      documentKey,
      editorSessionKey,
      episodeId,
      questionnaireId,
      geminiAiDisabled,
      onPlanGenerated,
      diagnoses,
      symptoms,
      operation,
      treatmentFrequency,
    };
    if (mode === 'quick') {
      assistant.startQuickTreatmentPlanFlow(context);
    } else {
      assistant.startGuidedTreatmentPlanFlow(context);
    }
  };

  const openRevise = () => {
    if (!assistant || geminiAiDisabled) return;
    assistant.startTreatmentPlanRevisionFlow();
  };

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon size="sm" variant="light" color="gold" radius="xl">
              <IconSparkles size={12} />
            </ThemeIcon>
            <Title order={5}>AI treatment plan</Title>
          </Group>
          {geminiAiDisabled && (
            <Badge size="xs" variant="light" color="gray">
              Disabled
            </Badge>
          )}
        </Group>

        {geminiAiDisabled ? (
          <Text size="xs" c="gray.7">
            AI documentation assistance is disabled for this provider or client. You can still complete the
            treatment plan manually.
          </Text>
        ) : (
          <>
            <Text size="xs" c="gray.7">
              {showRevise
                ? 'Revise this draft, or use the guided planner to build another plan from a longer interview.'
                : 'Draft from the intake note and Zoom transcript when available, or use a longer guided interview.'}
            </Text>
            {generatedApplied && (
              <Group gap={6} wrap="nowrap">
                <ThemeIcon size="sm" variant="light" color="green" radius="xl">
                  <IconCheck size={12} />
                </ThemeIcon>
                <Text size="xs" c="green.8">
                  Draft is ready — revise anything that needs adjustment.
                </Text>
              </Group>
            )}
            <Group gap="xs" wrap="wrap">
              {showRevise ? (
                <Button
                  variant="filled"
                  color="gold"
                  leftSection={<IconSparkles size={16} />}
                  onClick={openRevise}
                  disabled={!canRevise}
                >
                  Revise with AI
                </Button>
              ) : (
                <Button
                  variant="filled"
                  color="gold"
                  leftSection={<IconBolt size={16} />}
                  onClick={() => openPlanner('quick')}
                  disabled={!ready || !assistant}
                >
                  Quick generate
                </Button>
              )}
              <Button
                variant="light"
                color="gold"
                leftSection={<IconSparkles size={16} />}
                onClick={() => openPlanner('guided')}
                disabled={!ready || !assistant}
              >
                Open guided planner
              </Button>
            </Group>
            {!showRevise && (
              <Text size="xs" c="dimmed">
                Quick generate usually asks only 1–3 confirmation questions. After a draft exists, the
                primary action becomes Revise with AI; guided planner stays available.
              </Text>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
